import { defineComponent as _defineComponent } from 'vue'
import {
    computed,
    onMounted,
    ref,
    watch,
  } from "vue";
  import { useStore } from "vuex";
  import router from "@/router";
  import { heightAnimation } from "@/helpers/heightAnimation";
  import plural from "@/features/plural";
  import { formatDate } from "@/helpers/date";
  import { LotStatus } from "@/enums/LotStatus";
  import { getLotCustomerMapStatusClass } from "@/helpers/status";
  import { useEstateVersionRedirect } from "@/composables/useEstateVersionRedirect";
  import { UserRoles } from "@/enums/UserRoles";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import { formatPrice } from "@/helpers/transformation/price";
  import StageNavMobile from "@/components/StageNavMobile.vue";
  import { StageMapInterface } from "@/types/StageMapInterface";
  import { LotMapInterface } from "@/types/LotMapInterface";
  import { EstateInterface } from "@/types/EstateInterface";
  import { StageStatus } from "@/enums/StageStatus";
  import { UiBasicStageInterface } from "@/types/StageInterface";
  import LotLifecycle from "./LotLifecycle.vue";
  import { toastError } from "@/helpers/toastError";
  import { POSITION, useToast } from "vue-toastification";
  import { LotInterface } from "@/types/LotInterface";
  import LotMapsWithStageInterface from "@/types/LotMapsWithStageInterface";

  const mobileLayoutWidth = 680;
  interface Props {
    estate: EstateInterface;
    isEmbedded: boolean;
    viewType: string;
    mapPage: string;
    isBuilderView?: boolean;
    lotSelected?: LotMapInterface;
    hideNav?: boolean;
    hideStageRowHeader?: boolean;
    showSoldOutStages?: boolean;
    stageId?: string;
    lotsByHouseDesigns?: LotInterface[];
    isMapModalView?: boolean;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'StageList',
  props: {
    estate: {},
    isEmbedded: { type: Boolean },
    viewType: {},
    mapPage: {},
    isBuilderView: { type: Boolean },
    lotSelected: {},
    hideNav: { type: Boolean },
    hideStageRowHeader: { type: Boolean },
    showSoldOutStages: { type: Boolean },
    stageId: {},
    lotsByHouseDesigns: {},
    isMapModalView: { type: Boolean }
  },
  emits: [
    "reserve",
    "changeView",
    "selectLot",
    "changeSize",
  ],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const emit = __emit;
  const { isDesktop } = useIsDesktop(mobileLayoutWidth);
  const store = useStore();
  const toast = useToast();
  const { estateId } = router.currentRoute.value.params;
  const lotsList = ref<LotMapsWithStageInterface[]>([]);
  const activeStagesIndex = ref<boolean[]>([]);
  const tableRefs = ref([]);
  const loading = ref(true);

  const props = __props;
  const stages = ref<StageMapInterface[]>([]);

  const getNumberOfLots = computed(() => {
    return lotsList.value.reduce((value, lot) => value + lot.lotData.length, 0);
  });

  const lotIdSelected = computed(() => {
    return props.lotSelected?.lotId._id;
  });

  const { checkEstateVersionAndRedirect } = useEstateVersionRedirect();

  onMounted(() => {
    checkEstateVersionAndRedirect(estateId as string, UserRoles.CUSTOMER);
    getLotsList();
  });

  const getStageTitle = (index: number, isBuilderView: boolean) => {
    const lots = lotsList.value[index].lotData;
    const filteredLots = isBuilderView
      ? lots.filter((lot) => lot.lotId.isBuildersEnabled)
      : lots;

    const availableLots =
      isBuilderView && !props.isMapModalView
        ? filteredLots.length - 1
        : filteredLots.length;
    const lotsText = props.isMapModalView ? "Suitable Lots" : "Available Lots";

    if (availableLots === 0) {
      return "(Sold Out)";
    }

    return `(${availableLots} ${lotsText})`;
  };

  const onStageClick = (stage: UiBasicStageInterface, lot: LotMapInterface) => {
    const event = props.isBuilderView ? "selectLot" : "reserve";
    const value = { ...lot, stageId: stage };

    emit(event, value);
  };

  const getLotsList = () => {
    store
      .dispatch("lot/getLotsList", {
        estateId,
        isCustomer: true,
        soldOutStatus: props.showSoldOutStages,
      })
      .then(async (res) => {
        lotsList.value = res.data;
        initializeLotsWithStageList();
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const initializeLotsWithStageList = () => {
    filterLotsFromStageProp();
    filterLotsInStageFromHouseDesignLots();
    lotsList.value.forEach(() => {
      activeStagesIndex.value.push(true);
    });
  };

  const getLotPath = (path: string) => {
    if (path.startsWith("M", 0) || path.startsWith("m", 0)) {
      return path;
    }

    return "";
  };

  const filterLotsInStageFromHouseDesignLots = () => {
    if (!props.lotsByHouseDesigns) return;

    let lotIds: string[] = props.lotsByHouseDesigns.map((lot) => lot._id);

    lotsList.value = lotsList.value.map((lotsAndStage) =>
      filterlotsAndStageByLotIds(lotsAndStage, lotIds)
    );
  };

  const filterlotsAndStageByLotIds = (
    lotsAndStage: LotMapsWithStageInterface,
    lotIds: string[]
  ): LotMapsWithStageInterface => {
    const matchingLotData = lotsAndStage.lotData.filter((lot) =>
      lotIds.includes(lot.lotId._id)
    );

    return { ...lotsAndStage, lotData: matchingLotData };
  };

  const filterLotsFromStageProp = () => {
    if (props.stageId) {
      lotsList.value = lotsList.value.filter(
        (l) => l.stage.id === props.stageId
      );
    }
  };

  const filteredLotData = (lotData: LotMapInterface[]) => {
    if (!props.isBuilderView) {
      return lotData;
    }

    return lotData.filter(
      (lot: { lotId?: { isBuildersEnabled?: boolean; _id?: string } }) => {
        return (
          lot.lotId?.isBuildersEnabled && lot.lotId?._id !== lotIdSelected.value
        );
      }
    );
  };

  const onLotRow = (index: number) => {
    activeStagesIndex.value[index] = !activeStagesIndex.value[index];
    const section = tableRefs.value[index];

    heightAnimation(section);
    emit("changeSize");
  };

  const getLotStatus = (lotStatus: string) => {
    const statusMapping = new Map<string, string>([
      [LotStatus.EOI, LotStatus.HOLD],
      [LotStatus.CONTRACT_EXECUTED, LotStatus.UNDER_CONTRACT],
      [LotStatus.UNCONDITIONAL, LotStatus.SOLD],
      [LotStatus.SETTLED, LotStatus.SOLD],
      [LotStatus.OPTION, LotStatus.SOLD],
      [LotStatus.OFFLINE, LotStatus.SOLD],
    ]);

    return statusMapping.get(lotStatus) ?? lotStatus;
  };

  watch(
    () => props.stageId,
    () => getLotsList()
  );

const __returned__ = { mobileLayoutWidth, emit, isDesktop, store, toast, estateId, lotsList, activeStagesIndex, tableRefs, loading, props, stages, getNumberOfLots, lotIdSelected, checkEstateVersionAndRedirect, getStageTitle, onStageClick, getLotsList, initializeLotsWithStageList, getLotPath, filterLotsInStageFromHouseDesignLots, filterlotsAndStageByLotIds, filterLotsFromStageProp, filteredLotData, onLotRow, getLotStatus, computed, onMounted, ref, watch, get useStore() { return useStore }, get router() { return router }, get heightAnimation() { return heightAnimation }, get plural() { return plural }, get formatDate() { return formatDate }, get LotStatus() { return LotStatus }, get getLotCustomerMapStatusClass() { return getLotCustomerMapStatusClass }, get useEstateVersionRedirect() { return useEstateVersionRedirect }, get UserRoles() { return UserRoles }, get useIsDesktop() { return useIsDesktop }, get formatPrice() { return formatPrice }, StageNavMobile, get StageMapInterface() { return StageMapInterface }, get LotMapInterface() { return LotMapInterface }, get EstateInterface() { return EstateInterface }, get StageStatus() { return StageStatus }, get UiBasicStageInterface() { return UiBasicStageInterface }, LotLifecycle, get toastError() { return toastError }, get POSITION() { return POSITION }, get useToast() { return useToast }, get LotInterface() { return LotInterface }, get LotMapsWithStageInterface() { return LotMapsWithStageInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})